/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import { isValid, parse } from 'date-fns';

/* eslint-disable import/prefer-default-export */

// Ideally these should come from content stack
const occasionList = {
    1: { desc: 'Anniversary', id: 1 },
    2: { desc: 'Birthday', id: 2 },
    19: { desc: "Boss' Day", id: 19 },
    10: { desc: 'Christmas', id: 10 },
    17: { desc: 'Cinco de Mayo', id: 17 },
    21: { desc: 'Diwali', id: 21 },
    6: { desc: 'Easter', id: 6 },
    20: { desc: 'Eid', id: 20 },
    8: { desc: 'Father’s Day', id: 8 },
    12: { desc: 'Friendship Day', id: 12 },
    14: { desc: 'Halloween', id: 14 },
    18: { desc: 'Hannukah', id: 18 },
    4: { desc: 'Just Because', id: 4 },
    16: { desc: 'Memorial Day', id: 16 },
    7: { desc: 'Mother’s Day', id: 7 },
    13: { desc: 'New Year’s', id: 13 },
    11: { desc: "Parents' Day", id: 11 },
    9: { desc: 'Thanksgiving', id: 9 },
    5: { desc: 'Valentine’s Day', id: 5 },
    15: { desc: 'Veteran’s Day', id: 15 }
};

// Ideally this is dynamic based on the occastion list above
const holidayOccasionList = {
    19: { desc: "Boss' Day", id: 19 },
    17: { desc: 'Cinco de Mayo', id: 17 },
    10: { desc: 'Christmas', id: 10 },
    21: { desc: 'Diwali', id: 21 },
    6: { desc: 'Easter', id: 6 },
    20: { desc: 'Eid', id: 20 },
    8: { desc: 'Father’s Day', id: 8 },
    12: { desc: 'Friendship Day', id: 12 },
    14: { desc: 'Halloween', id: 14 },
    18: { desc: 'Hannukah', id: 18 },
    16: { desc: 'Memorial Day', id: 16 },
    7: { desc: 'Mother’s Day', id: 7 },
    13: { desc: 'New Year’s', id: 13 },
    11: { desc: "Parents' Day", id: 11 },
    9: { desc: 'Thanksgiving', id: 9 },
    5: { desc: 'Valentine’s Day', id: 5 },
    15: { desc: 'Veteran’s Day', id: 15 }
};

export const isOccasionHoliday = (occasionId) => occasionId in holidayOccasionList;

export const buildRecipientPayload = ({ recipients, customer, brandId, email }) => {
    const { firstName, lastName } = customer;
    return recipients.map((recipient) => ({
        rcpEmail: email,
        firstName: recipient?.rcpFirstName || '',
        lastName: recipient?.rcpLastName || '',
        rcpFirstName: recipient?.rcpFirstName || '',
        rcpLastName: recipient?.rcpLastName || '',
        occasions: recipient?.occasions?.map((occasion) => {
            const [dateMM, dateDD] = (occasion?.date || '').split('/');
            return {
                brandId: brandId || '1001',
                createdUser: 'SSP',
                custFirstName: firstName || '',
                custLastName: lastName || '',
                occasion: occasion?.occasion || '',
                dateDD: dateDD || '',
                dateMM: dateMM || '',
                dateYYYY: '',
                desc: occasionList[occasion?.occasion]?.desc || '',
                reminderEmail: email,
                reminderPhone: '',
                subscribed: true,
                subscribedEmail: true,
                subscribedPhone: false,
            };
        }) || [],
    }));
};

export const isValidDate = (dateStr) => {
    const mmDdRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
    if (!mmDdRegex.test(dateStr)) {
        return false;
    }
    // If the date has a valid format, I'm revalidating using date-fns
    const [month, day] = dateStr.split('/');
    const currentYear = new Date().getFullYear();
    const date = parse(`${month}/${day}/${currentYear}`, 'MM/dd/yyyy', new Date());
    return isValid(date);
};

// Helper function to get the next upcoming holiday
const getNextUpcomingHoliday = (currentDate = new Date()) => {
    const holidays2025 = [
        { id: 5, desc: "Valentine’s Day", date: new Date(2025, 1, 14) }, // Feb 14, 2025
        { id: 6, desc: "Easter", date: new Date(2025, 3, 20) }, // Apr 20, 2025
        { id: 7, desc: "Mother’s Day", date: new Date(2025, 4, 11) }, // May 11, 2025
        { id: 8, desc: "Father’s Day", date: new Date(2025, 5, 15) }, // Jun 15, 2025
        { id: 14, desc: "Halloween", date: new Date(2025, 9, 31) }, // Oct 31, 2025
        { id: 9, desc: "Thanksgiving", date: new Date(2025, 10, 27) }, // Nov 27, 2025
        { id: 10, desc: "Christmas", date: new Date(2025, 11, 25) }, // Dec 25, 2025
        { id: 13, desc: "New Year’s", date: new Date(2026, 0, 1) }, // Jan 1, 2026
    ];

    holidays2025.sort((a, b) => a.date - b.date);
    const nextHoliday = holidays2025.find((holiday) => holiday.date > currentDate);
    return nextHoliday || holidays2025[0]; // Fallback to first holiday
};

// Fixed getDefaultOccasion to return index in occasionList
export const getDefaultOccasion = (manualDefaultHolidayId, sortedOccasions) => {
    const allowedHolidayIds = [5, 6, 7, 8, 9, 10, 13, 14];

    let defaultOccasionIndex = 0; // Default to placeholder
    if (manualDefaultHolidayId && allowedHolidayIds.includes(Number(manualDefaultHolidayId))) {
        defaultOccasionIndex = sortedOccasions.findIndex((occasion) => occasion.occasion === manualDefaultHolidayId.toString());
    } else {
        const nextHoliday = getNextUpcomingHoliday();
        defaultOccasionIndex = sortedOccasions.findIndex((occasion) => occasion.occasion === nextHoliday.id.toString());
    }

    // Adjust for placeholder at index 0
    return defaultOccasionIndex >= 0 ? defaultOccasionIndex : 0;
};

export const sortOccasions = (occasions) => {
  // Find the index of the "Occasion" item
  const occasionIndex = occasions.findIndex((occasion) => occasion.desc === 'Occasion');
  const occasionItem = occasions[occasionIndex];

  // Filter out the "Occasion" item, sort the rest, then reinsert "Occasion" at its original index
  const sorted = occasions
    .filter((occasion) => occasion.desc !== 'Occasion')
    .sort((a, b) => a.desc.localeCompare(b.desc));

  if (occasionIndex !== -1) {
    sorted.splice(occasionIndex, 0, occasionItem);
  }

  return sorted;
};

export const getOccasionList = () => {
    const occasionsMapped = [
        {
            occasion: '',
            desc: 'Occasion',
            dateMM: '',
            dateDD: '',
            dateYYYY: '',
            occasionId: '',
            reminderPhone: '',
            reminderEmail: '',
            subscribedEmail: false,
            subscribedPhone: false,
            subscribed: false,
        },
    ];

    if (Object.keys(occasionList).length > 0) {
        const mappedOccasions = Object.entries(occasionList).map(([_, occasion]) => ({
            occasion: occasion.id.toString(),
            desc: occasion.desc,
            dateMM: '',
            dateDD: '',
            dateYYYY: '',
            occasionId: '',
            reminderPhone: '',
            reminderEmail: '',
            subscribedEmail: false,
            subscribedPhone: false,
            subscribed: false,
        }));
        occasionsMapped.push(...mappedOccasions);
    }

    return sortOccasions(occasionsMapped);
};